/**
 * Read-only display for POST /admin/tickets/refund-breakdown response, worded
 * for agents: what the customer paid, what we keep, and where the refund goes.
 * @param {object} props
 * @param {object} props.data - API `data` object
 * @param {(n: unknown) => string} props.formatINR
 * @param {string} [props.title] - e.g. "Flight 2 · HYD → MAA" on a whole-trip ticket
 * @param {boolean} [props.collapsible] - start as the one-line summary; details open on tap
 */
import { useState } from "react";

const BRANCH_LABELS = {
  non_dgca_standard: "Normal cancellation",
  dgca_flight_operational: "Free cancellation (DGCA 48-hour rule)",
  dgca_flight_cancelled: "Airline cancelled the flight (DGCA)",
  instant_booking_failed_full_cash: "Booking failed: full refund",
  no_booking: "No active booking",
};

const CASHBACK_STATUS_TEXT = {
  held_balance: "still in the customer's wallet",
  partially_held: "partly still in the customer's wallet",
  released: "already released to the customer's wallet",
  none: "not given",
};

const num = (v) => Number(v) || 0;

function Row({ label, hint, value, strong = false }) {
  return (
    <div className="flex justify-between gap-2">
      <span className="text-gray-700">
        {label}
        {hint ? <span className="block text-xs text-gray-400">{hint}</span> : null}
      </span>
      <span className={`tabular-nums ${strong ? "font-bold" : "font-semibold"}`}>{value}</span>
    </div>
  );
}

export default function RefundBreakdownPanel({ data, formatINR, title = null, collapsible = false }) {
  const [open, setOpen] = useState(!collapsible);
  if (!data) return null;

  const paid = data.paid || {};
  const cb = data.cashback_earned || {};
  const ded = data.deductions || {};
  const split = data.refund_split || {};
  const warnings = Array.isArray(data.warnings) ? data.warnings : [];

  const totalFare = num(paid.total_fare);
  const convFee = num(paid.convenience_fee_total ?? paid.convenience_fee);
  const airlineCharge = num(ded.tbo_cancellation_charge ?? ded.airline_cancellation_charge);
  const convKept = num(ded.convenience_fee_forfeited);
  const serviceFee = num(ded.airline_service_fee);
  const promoKept = num(ded.promo_forfeited);
  const totalKept = num(split.total_forfeited);
  const gets = num(split.total_customer_receives);
  const cashAmt = num(split.cash_to_razorpay ?? split.cash_to_pg);
  const gcAmt = num(split.giftcard_to_wallet);
  const navAmt = num(split.navcash_to_wallet);
  const isGiftCardOnly = gcAmt > 0 && cashAmt === 0 && navAmt === 0;
  const cashbackTakenBack = num(split.cashback_clawback ?? cb.clawback_amount);
  const cashbackGiven = num(cb.amount) || cashbackTakenBack;

  const branchLabel = data.calculation_branch
    ? BRANCH_LABELS[data.calculation_branch] || String(data.calculation_branch).replace(/_/g, " ")
    : null;

  return (
    <div className="mt-4 space-y-4 rounded-2xl border border-blue-200 bg-white p-4 shadow-sm">
      <div className={`space-y-2 ${open ? "border-b border-gray-100 pb-3" : ""}`}>
        <div className="flex flex-wrap items-center gap-2">
          {title ? <span className="text-sm font-bold text-gray-900">{title}</span> : null}
          {branchLabel ? (
            <span className="inline-flex items-center rounded-full border border-blue-300 bg-blue-50 px-2.5 py-0.5 text-xs font-semibold text-blue-800">
              {branchLabel}
            </span>
          ) : null}
        </div>
        <p className="text-sm text-gray-800" data-testid="refund-summary">
          Customer paid <strong>{formatINR(totalFare)}</strong>. We keep <strong>{formatINR(totalKept)}</strong>.
          Customer gets <strong>{formatINR(gets)}</strong> back.
        </p>
        {collapsible ? (
          <button
            type="button"
            aria-expanded={open}
            onClick={() => setOpen((o) => !o)}
            className="text-xs font-semibold text-blue-700 hover:underline"
          >
            {open ? "Hide details ▴" : "Show details ▾"}
          </button>
        ) : null}
        {open && data.calculation_explanation ? (
          <details className="text-xs text-gray-500">
            <summary className="cursor-pointer select-none">Show how this was calculated</summary>
            <p className="mt-1 leading-relaxed">{data.calculation_explanation}</p>
          </details>
        ) : null}
      </div>

      {open && (
      <>

      <div>
        <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">What the customer paid</div>
        <div className="rounded-xl border border-gray-200 bg-gray-50 p-3 space-y-2 text-sm">
          <Row label="Ticket price" value={formatINR(totalFare)} strong />
          {num(paid.cash_paid) > 0 && <Row label="Paid by card / UPI / bank" value={formatINR(paid.cash_paid)} />}
          {num(paid.navcash_used) > 0 && <Row label="Paid with NavCash" value={formatINR(paid.navcash_used)} />}
          {num(paid.giftcard_used) > 0 && <Row label="Paid with gift card" value={formatINR(paid.giftcard_used)} />}
          {num(paid.promo_discount) > 0 && <Row label="Promo discount" value={formatINR(paid.promo_discount)} />}
          {/* The GST-inclusive fee, the same figure that is kept below.
              paid.convenience_fee is the pre-GST base (₹324.55 next to a
              ₹382.97 deduction looked like the customer was overcharged). */}
          {convFee > 0 && (
            <div className="border-t border-gray-200 pt-2">
              <Row
                label="Our booking fee (included in the price)"
                hint={num(paid.convenience_fee_gst) > 0 ? `${formatINR(paid.convenience_fee)} + GST ${formatINR(paid.convenience_fee_gst)}` : null}
                value={formatINR(convFee)}
              />
            </div>
          )}
        </div>
      </div>

      <div>
        <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">What we keep (not refunded)</div>
        <div className="rounded-xl border border-gray-200 bg-gray-50 p-3 space-y-2 text-sm">
          <Row label="Airline cancellation charge" hint={airlineCharge === 0 ? "Nothing charged by the airline" : null} value={formatINR(airlineCharge)} />
          {convKept > 0 && <Row label="Our booking fee (not refundable)" value={formatINR(convKept)} />}
          {serviceFee > 0 && <Row label="Airline service fee (not refundable)" value={formatINR(serviceFee)} />}
          {promoKept > 0 && <Row label="Promo discount taken back" value={formatINR(promoKept)} />}
          <div className="border-t border-gray-200 pt-2">
            <Row label="Total we keep" value={formatINR(totalKept)} strong />
          </div>
        </div>
      </div>

      <div className={`rounded-2xl border-2 p-4 space-y-3 ${isGiftCardOnly ? "border-purple-300 bg-purple-50/40" : "border-blue-300 bg-blue-50/40"}`}>
        <div className={`text-xs font-bold uppercase tracking-wide ${isGiftCardOnly ? "text-purple-900" : "text-blue-900"}`}>
          Where the refund goes
        </div>
        {isGiftCardOnly && (
          <div className="rounded-lg border border-purple-300 bg-purple-100 px-2.5 py-2 text-xs text-purple-900 font-medium">
            Paid with a gift card, so the refund goes to the customer&apos;s app wallet, not to their bank.
          </div>
        )}
        <div className="space-y-2 text-sm">
          {cashAmt > 0 && <Row label="Back to the customer's card / UPI / bank" value={formatINR(cashAmt)} />}
          {navAmt > 0 && <Row label="Back to NavCash wallet" value={formatINR(navAmt)} />}
          {gcAmt > 0 && <Row label="Gift card amount back to app wallet" value={formatINR(gcAmt)} />}
          <div className={`flex justify-between gap-2 border-t pt-3 mt-1 ${isGiftCardOnly ? "border-purple-200" : "border-blue-200"}`}>
            <span className="text-base font-bold text-gray-900">Customer gets back</span>
            <span className={`text-xl font-extrabold tabular-nums ${isGiftCardOnly ? "text-purple-900" : "text-gray-900"}`}>
              {formatINR(gets)}
            </span>
          </div>
          {/* Not money paid out: it only reverses cashback still pending in the
              wallet, so it sits outside the refund. */}
          {cashbackTakenBack > 0 ? (
            <div className="flex justify-between gap-2 text-xs text-amber-800 border-t border-blue-100 pt-2">
              <span>
                Cashback taken back from wallet
                <span className="block text-amber-700/80">
                  Cashback of {formatINR(cashbackGiven)} from this booking is {CASHBACK_STATUS_TEXT[cb.currently_in] || "in the wallet"}.
                  It is taken back from the wallet, not from this refund.
                </span>
              </span>
              <span className="font-semibold tabular-nums">− {formatINR(cashbackTakenBack)}</span>
            </div>
          ) : null}
        </div>
      </div>

      </>
      )}

      {warnings.length > 0 ? (
        <div className="rounded-xl border border-yellow-300 bg-yellow-50 p-3 text-sm text-yellow-900">
          <div className="text-xs font-bold uppercase tracking-wide text-yellow-800 mb-2">Please check</div>
          <ul className="list-disc space-y-1 pl-4 text-xs leading-relaxed">
            {warnings.map((w, i) => (
              <li key={i}>{w}</li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
